/**
 * #976 — wraps a card-annotation client so a 403 whose code says the page's
 * scope or target is no longer good (`CARD_SCOPE_INVALID`,
 * `CARD_MEMBERSHIP_MISSING`, `CARD_TARGET_UNAVAILABLE`) tells the composer to
 * re-read the scope. The adapters map 403 to `forbidden` without a code, so
 * this must sit on the client, not the adapter. `readScope` is NOT wrapped: a
 * refused scope read must never trigger another one (loop guard).
 *
 * Relative imports only.
 */
import { CardAnnotationClientError, type CardAnnotationClient } from './client';

const REFUSAL_CODES = new Set(['CARD_SCOPE_INVALID', 'CARD_MEMBERSHIP_MISSING', 'CARD_TARGET_UNAVAILABLE']);

type Wrapped = 'readNotes' | 'saveNote' | 'readAssignments' | 'assign' | 'listSources' | 'createSource';
const WRAPPED: readonly Wrapped[] = ['readNotes', 'saveNote', 'readAssignments', 'assign', 'listSources', 'createSource'];

export function watchRefusals(client: CardAnnotationClient, onRefused: () => void): CardAnnotationClient {
  const out = { ...client } as Record<string, unknown>;
  for (const name of WRAPPED) {
    const original = client[name] as (...args: unknown[]) => Promise<unknown>;
    out[name] = async (...args: unknown[]) => {
      try {
        return await original(...args);
      } catch (error) {
        if (error instanceof CardAnnotationClientError && error.status === 403 && REFUSAL_CODES.has(error.code)) onRefused();
        throw error;
      }
    };
  }
  return out as unknown as CardAnnotationClient;
}
